import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'

import { authAPI } from '@/api/new-api'
import {
  BuildingStaffModel,
  BuildingStaffType,
  BuildingStaffTypeEnum,
} from '@/models/features/building.models'

export interface GetBuildingStaffRequest {
  buildingId: string | number
  type: BuildingStaffType
}

export const getBuildingStaffQueryKey = (data: GetBuildingStaffRequest) => [
  'useGetBuildingStaff',
  data,
]

export const getBuildingStaff = async ({ buildingId, type }: GetBuildingStaffRequest) => {
  const response = await authAPI.get<BuildingStaffModel[]>(`/building/${buildingId}/${type}`)
  return response.data
}

export interface useGetBuildingStaffProps {
  buildingId: string | number
  types?: BuildingStaffType[]
}

export const useGetBuildingStaff = ({
  buildingId,
  types = [...BuildingStaffTypeEnum.options],
}: useGetBuildingStaffProps) => {
  const results = useQueries({
    queries: types.map((type) => ({
      enabled: !!buildingId,
      queryFn: () => getBuildingStaff({ buildingId, type }),
      queryKey: getBuildingStaffQueryKey({ buildingId, type }),
    })),
  })

  const { data, errors, isError, isFetching, isLoading } = useMemo(() => {
    return results.reduce(
      (acc, result) => {
        if (result.isLoading) acc.isLoading = true
        if (result.isFetching) acc.isFetching = true
        if (result.isError) acc.isError = true
        if (result.error) acc.errors.push(result.error)
        if (result.data) acc.data = acc.data.concat(result.data)
        return acc
      },
      {
        data: [] as BuildingStaffModel[],
        errors: [] as Error[],
        isError: false,
        isFetching: false,
        isLoading: false,
      },
    )
  }, [results])

  return {
    data,
    errors,
    isError,
    isFetching,
    isLoading,
  }
}
